/**
 * A treemap's layout: rectangles whose areas are in proportion to their values,
 * tiling a box exactly.
 *
 * Squarified (Bruls, Huizing & van Wijk): the items go in biggest first, laid in
 * strips along the box's shorter side, and a strip takes the next item only
 * while that keeps its rectangles closer to square. Long thin slivers are what
 * make a treemap unreadable, and this is the standard way of avoiding them.
 *
 * Pure: pixels in, pixels out. Nothing here knows what the items are.
 */

/** The worst aspect ratio in a strip of `values`, laid along a side of `side`. */
function worst(values, side) {
  const sum = values.reduce((a, b) => a + b, 0)
  if (!sum || !side) return Infinity
  let max = 0
  let min = Infinity
  for (const v of values) {
    if (v > max) max = v
    if (v < min) min = v
  }
  const s2 = side * side
  const sum2 = sum * sum
  return Math.max((s2 * max) / sum2, sum2 / (s2 * min))
}

/**
 * @param {{value: number}[]} items  anything with a positive `value`
 * @param {number} width
 * @param {number} height
 * @returns {object[]} the items, biggest first, each with `x`, `y`, `w`, `h`
 *   added; items of no value are left out, since they have no area to take
 */
export function squarify(items, width, height) {
  const kept = (items ?? [])
    .filter((item) => Number.isFinite(item?.value) && item.value > 0)
    .sort((a, b) => b.value - a.value)
  if (!kept.length || !(width > 0) || !(height > 0)) return []

  // Work in areas: each item's share of the box.
  const total = kept.reduce((sum, item) => sum + item.value, 0)
  const scale = (width * height) / total
  const queue = kept.map((item) => ({ item, area: item.value * scale }))

  const out = []
  let box = { x: 0, y: 0, w: width, h: height }

  /** Lays one strip along the shorter side and shrinks the box past it. */
  const place = (strip) => {
    const areas = strip.map((entry) => entry.area)
    const sum = areas.reduce((a, b) => a + b, 0)
    if (box.w >= box.h) {
      // A column down the left edge.
      const w = sum / box.h
      let y = box.y
      for (const entry of strip) {
        const h = entry.area / w
        out.push({ ...entry.item, x: box.x, y, w, h })
        y += h
      }
      box = { x: box.x + w, y: box.y, w: box.w - w, h: box.h }
    } else {
      // A row along the top edge.
      const h = sum / box.w
      let x = box.x
      for (const entry of strip) {
        const w = entry.area / h
        out.push({ ...entry.item, x, y: box.y, w, h })
        x += w
      }
      box = { x: box.x, y: box.y + h, w: box.w, h: box.h - h }
    }
  }

  let strip = []
  for (const entry of queue) {
    const side = Math.min(box.w, box.h)
    const current = strip.map((e) => e.area)
    if (!strip.length || worst([...current, entry.area], side) <= worst(current, side)) {
      strip.push(entry)
    } else {
      place(strip)
      strip = [entry]
    }
  }
  if (strip.length) place(strip)
  return out
}
